import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import {
  colorMap,
  getDefaultFilters,
  listVisibleReports,
  listVisibleReportsByCategory,
} from './reportConfig.js';
import { formatCurrencyWhole } from '../../utils/formatCurrency.js';

const MONEY_KEYS = new Set([
  'total', 'paid', 'balance', 'freight', 'amount', 'commission', 'commission_amount',
  'paid_amount', 'unpaid_amount', 'freight_base', 'owner_share', 'pay_due', 'get_due',
  'net_salary', 'handling', 'other_charges', 'to_pay', 'settlement',
  'tds_amount', 'net_amount', 'booking_total', 'on_account_total', 'delivery_total', 'cum_total',
  'nrr_day', 'nrr_cum', 'booking_commission', 'delivery_commission', 'truck_hire',
  'salary_net_total', 'gross_business', 'amount_received', 'net_os',
  'sub_total', 'sgst', 'cgst', 'igst',
  'other_charges', 'to_pay_amount', 'total_amount', 'on_account_amount', 'discount',
  'net_receivable', 'booking_commission', 'delivery_commission',
]);

const STAT_KEYS_BY_TYPE = {
  user: ['shipments', 'total', 'paid'],
  location: ['paid_amount', 'to_pay_amount', 'on_account_amount'],
  vehicle: ['shipments', 'amount'],
  'ml-sheet': ['shipments', 'total', 'paid', 'balance'],
  shipment: ['articles', 'discount', 'total', 'paid', 'balance'],
  business: ['freight', 'discount', 'total_amount', 'paid_amount', 'on_account_amount'],
  'daily-business': ['booking_total', 'on_account_total', 'delivery_total', 'cum_total'],
  'route-pl': ['nrr_day', 'nrr_cum'],
  receive: ['freight', 'total', 'articles'],
  'delivery-event': ['freight', 'total', 'paid', 'balance'],
  receivable: ['gross_business', 'amount_received', 'net_os'],
  payable: ['previous_os', 'today_expense', 'payment_made', 'final_os'],
  'approved-transactions': ['amount'],
  'goods-balance': ['gcs', 'articles', 'freight', 'total'],
  'onaccount-credit': ['gcs', 'total', 'paid', 'balance'],
  'party-booking': ['freight', 'total', 'articles'],
  'invoice-cancellation': ['total'],
  'branch-payable': ['total', 'booking_commission', 'delivery_commission', 'truck_hire'],
  expense: ['amount'],
  'on-contract': [],
  'vehicle-contract': ['total', 'paid', 'balance'],
  'trip-contract': ['total', 'paid', 'balance'],
  commission: ['commission_amount', 'tds_amount', 'net_amount', 'settlement', 'unpaid_amount'],
  'agent-tds': ['commission_amount', 'tds_amount'],
  'gst-collection': ['sub_total', 'sgst', 'total'],
  'external-commission': ['freight', 'commission', 'settlement'],
  hr: ['salary_count', 'salary_net_total'],
};

const LABEL_MAP = {
  shipments: 'Shipments',
  articles: 'Articles',
  total: 'Total',
  paid: 'Paid',
  balance: 'Balance',
  freight: 'Freight',
  discount: 'Discount',
  amount: 'Amount',
  commission: 'Commission',
  commission_amount: 'Commission',
  tds_amount: 'TDS',
  net_amount: 'Net',
  paid_amount: 'Paid',
  unpaid_amount: 'Due',
  freight_base: 'Freight',
  owner_share: 'Owner',
  pay_due: 'Pay Due',
  get_due: 'Get Due',
  net_salary: 'Net Salary',
  salary_count: 'Salaries',
  salary_net_total: 'Net Salary',
  handling: 'Handling',
  other_charges: 'Other',
  to_pay: 'ToPay',
  settlement: 'Settlement',
  booking_total: 'Booking',
  on_account_total: 'On Account',
  delivery_total: 'Delivery',
  cum_total: 'FY Cum',
  gross_business: 'Gross Business',
  other_charges: 'Other',
  to_pay_amount: 'To Pay',
  on_account_amount: 'On Account',
  total_amount: 'Total',
  net_receivable: 'Net Receivable',
  booking_commission: 'Book Comm',
  delivery_commission: 'Del Comm',
  sub_total: 'Sub Total',
  sgst: 'SGST',
  cgst: 'CGST',
  igst: 'IGST',
  amount_received: 'Received',
  net_os: 'Net O/s',
  nrr_day: 'NRR Day',
  nrr_cum: 'NRR Cum',
  gcs: 'GCs',
  truck_hire: 'Truck Hire',
};

function formatStatValue(key, value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isFinite(num) && MONEY_KEYS.has(key)) {
    return formatCurrencyWhole(num);
  }
  if (Number.isFinite(num)) return num.toLocaleString('en-IN');
  return String(value);
}

function buildCardStats(type, payload) {
  if (!payload) return [];
  const totals = payload.totals || {};
  const meta = payload.meta || {};
  const count = meta.count ?? (payload.rows || []).length ?? 0;
  const stats = [{ key: 'records', label: 'Records', value: count }];

  const preferred = STAT_KEYS_BY_TYPE[type] || Object.keys(totals);
  preferred.forEach((key) => {
    let val = totals[key];
    if (val == null && meta[key] != null) val = meta[key];
    if (val == null) return;
    stats.push({
      key,
      label: LABEL_MAP[key] || key.replace(/_/g, ' '),
      value: formatStatValue(key, val),
    });
  });

  return stats.slice(0, 4);
}

const DATA_STRIP = {
  bg: '#f8fafc',
  border: '#e2e8f0',
  label: '#64748b',
  value: '#0f172a',
};

const HubCard = ({ def, onOpen, stats, loading }) => {
  const Icon = def.icon;
  const c = colorMap[def.color] || colorMap.indigo;
  const primary = stats[0];
  const rest = stats.slice(1, 4);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="report-card w-full text-left relative overflow-hidden
        rounded-xl border border-slate-200 bg-white
        shadow-sm transition-transform active:scale-[0.99] touch-manipulation
        flex flex-col min-h-[132px] sm:min-h-[140px]"
      style={{ fontFamily: 'inherit' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 8px 20px ${c.shadow}, 0 1px 4px rgba(15,23,42,0.05)`;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.borderColor = c.buttonBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl pointer-events-none"
        style={{ background: c.topBar }}
      />

      <div className="flex items-start gap-2.5 p-3.5 pb-2.5 flex-1">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: c.iconBg, border: `1px solid ${c.buttonBorder}` }}
        >
          <Icon className="w-4 h-4" style={{ color: c.iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-bold text-slate-900 m-0 leading-snug line-clamp-1">
            {def.title}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5 m-0 leading-snug line-clamp-2">
            {def.description}
          </p>
        </div>
      </div>

      <div
        className="mt-auto border-t px-3 py-2"
        style={{ background: DATA_STRIP.bg, borderColor: DATA_STRIP.border }}
      >
        {loading ? (
          <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-600">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 w-full">
            {[primary, ...rest].filter(Boolean).map((s) => {
              const text = String(s.value ?? '');
              const isMoney = text.startsWith('Rs.');
              const longValue = text.length > 14;
              const fullRow = text.length > 16;
              return (
                <div
                  key={s.key}
                  className={`min-w-0 text-left ${fullRow ? 'col-span-2' : ''}`}
                >
                  <p
                    className="text-[9px] uppercase tracking-wide font-semibold m-0 mb-0.5"
                    style={{ color: DATA_STRIP.label }}
                  >
                    {s.label}
                  </p>
                  <p
                    className={`font-extrabold m-0 leading-snug tabular-nums break-words ${
                      s.key === 'records' && !fullRow
                        ? 'text-base'
                        : longValue || isMoney
                          ? 'text-[11px]'
                          : 'text-sm'
                    }`}
                    style={{ color: DATA_STRIP.value, wordBreak: 'break-word' }}
                    title={text}
                  >
                    {s.value ?? 0}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </button>
  );
};

const Reports = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const cards = useMemo(() => listVisibleReports(user), [user]);
  const grouped = useMemo(() => listVisibleReportsByCategory(user), [user]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [summaryMap, setSummaryMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});

  const visibleGroups = useMemo(() => {
    if (activeCategory === 'all') return grouped;
    return grouped.filter((g) => g.id === activeCategory);
  }, [grouped, activeCategory]);

  useEffect(() => {
    if (!user || cards.length === 0) return undefined;
    let cancelled = false;

    const load = async () => {
      const nextLoading = {};
      cards.forEach((d) => { nextLoading[d.type] = true; });
      setLoadingMap(nextLoading);

      await Promise.all(
        cards.map(async (def) => {
          try {
            const params = { ...getDefaultFilters(def), summary_only: 1 };
            const res = await reportService.getReportData(def.type, params);
            const payload = res?.data || res;
            if (cancelled) return;
            setSummaryMap((prev) => ({
              ...prev,
              [def.type]: buildCardStats(def.type, payload),
            }));
          } catch {
            if (cancelled) return;
            setSummaryMap((prev) => ({
              ...prev,
              [def.type]: [{ key: 'records', label: 'Records', value: '—' }],
            }));
          } finally {
            if (!cancelled) {
              setLoadingMap((prev) => ({ ...prev, [def.type]: false }));
            }
          }
        }),
      );
    };

    load();
    return () => { cancelled = true; };
  }, [user, cards]);

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-slate-700" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 m-0 truncate">
                {t('reports.title')}
              </h1>
              <p className="text-xs text-slate-400 mt-0.5 m-0 font-medium line-clamp-2">
                {t('reports.subtitle', 'Organised by category — open a card for filters, PDF and Excel')}
              </p>
            </div>
          </div>
          <div className="inline-flex self-start sm:self-auto items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold text-slate-600">
              {cards.length} reports · {grouped.length} categories
            </span>
          </div>
        </div>

        {/* Category filter chips — light blue */}
        <div className="flex flex-wrap items-center gap-1.5 mb-5 pb-3 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors ${
              activeCategory === 'all'
                ? 'bg-sky-100 text-sky-800 border-sky-400 shadow-sm'
                : 'bg-sky-50/80 text-sky-700 border-sky-200 hover:bg-sky-100 hover:border-sky-300'
            }`}
          >
            All ({cards.length})
          </button>
          {grouped.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors ${
                activeCategory === cat.id
                  ? 'bg-sky-100 text-sky-800 border-sky-400 shadow-sm'
                  : 'bg-sky-50/80 text-sky-700 border-sky-200 hover:bg-sky-100 hover:border-sky-300'
              }`}
            >
              {cat.title} ({cat.reports.length})
            </button>
          ))}
        </div>

        <div className="space-y-7">
          {visibleGroups.map((cat) => (
            <section key={cat.id} className="min-w-0">
              <div className="flex items-end justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-extrabold text-slate-900 m-0 tracking-tight">
                    {cat.title}
                  </h2>
                  <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                    {cat.description}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {cat.reports.length} report{cat.reports.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {cat.reports.map((def) => (
                  <HubCard
                    key={def.type}
                    def={def}
                    stats={summaryMap[def.type] || []}
                    loading={!!loadingMap[def.type]}
                    onOpen={() => navigate(`/reports/${def.path || def.type}`)}
                  />
                ))}
              </div>
            </section>
          ))}

          {visibleGroups.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400">
              No reports available for this category.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
